import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { Staff, Machine, ShiftAssignment, Shift } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import OutlinedButton from "../../../components/Buttons/OutlinedButton";
import MesDateSelector, { getDefaultMesDate, saveMesDate } from "../../../components/MES/MesDateSelector";

interface StaffAssignmentByMachine {
  machine: Machine;
  assigned_staff: ShiftAssignment[];
  required_count: number;
}

/**
 * Staff Allocation - Assign workers to machines and shifts
 * Shows visual warnings for under-staffing
 */
const StaffAllocation = () => {
  savePathToLocalStorage();

  const [machines, setMachines] = useState<Machine[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [assignments, setAssignments] = useState<ShiftAssignment[]>([]);
  const [assignmentsByMachine, setAssignmentsByMachine] = useState<StaffAssignmentByMachine[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(getDefaultMesDate());
  const [selectedShift, setSelectedShift] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);

  // Drag and drop state
  const [draggedStaff, setDraggedStaff] = useState<Staff | null>(null);
  const [draggedAssignment, setDraggedAssignment] = useState<ShiftAssignment | null>(null);

  const handleDateChange = (date: string) => {
    saveMesDate(date);
    setSelectedDate(date);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedDate && selectedShift) {
      loadAssignments();
    }
  }, [selectedDate, selectedShift]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [machinesData, staffData, shiftsData] = await Promise.all([
        mesApi.getMachines(),
        mesApi.getStaff({ is_active: true }),
        mesApi.getShifts({ is_active: true }),
      ]);

      setMachines(machinesData);
      setStaff(staffData);
      setShifts(shiftsData);

      // Auto-select first shift
      if (shiftsData && shiftsData.length > 0) {
        setSelectedShift(shiftsData[0].shift_id);
      }
    } catch (err: any) {
      toast.error("Failed to load data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      const assignmentsData = await mesApi.getShiftAssignments(selectedDate, selectedDate);
      setAssignments(assignmentsData);
      groupAssignmentsByMachine(assignmentsData);
    } catch (err: any) {
      console.error("Failed to load assignments:", err);
      setAssignments([]);
      groupAssignmentsByMachine([]);
    }
  };

  const groupAssignmentsByMachine = (assignmentsData: ShiftAssignment[]) => {
    const grouped: StaffAssignmentByMachine[] = machines.map((machine) => {
      const machineAssignments = assignmentsData.filter(
        (a) => a.machine_id === machine.ID
      );

      // Default required count based on machine type
      let required = 1;
      if (machine.MachineType === "mixer") required = 2;
      if (machine.MachineType === "oven") required = 1;
      if (machine.MachineType === "packaging") required = 3;

      return {
        machine,
        assigned_staff: machineAssignments,
        required_count: required,
      };
    });

    setAssignmentsByMachine(grouped);
  };

  const handleDragStart = (
    e: React.DragEvent,
    staff?: Staff,
    assignment?: ShiftAssignment
  ) => {
    if (staff) {
      setDraggedStaff(staff);
      e.dataTransfer.effectAllowed = "move";
    } else if (assignment) {
      setDraggedAssignment(assignment);
      e.dataTransfer.effectAllowed = "move";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e: React.DragEvent, machine: Machine) => {
    e.preventDefault();

    if (!selectedShift) {
      toast.error("Please select a shift first");
      return;
    }

    try {
      if (draggedStaff) {
        // Assign unassigned staff to machine
        await mesApi.createShiftAssignment({
          staff_id: draggedStaff.staff_id,
          shift_id: selectedShift,
          machine_id: machine.ID,
          work_date: selectedDate,
        });
        toast.success(`Assigned ${draggedStaff.first_name} ${draggedStaff.last_name} to ${machine.MachineName}`);
        await loadAssignments();
      } else if (draggedAssignment) {
        // Move assignment to different machine
        await mesApi.updateShiftAssignment(draggedAssignment.assignment_id, {
          machine_id: machine.ID,
        });
        toast.success("Assignment moved");
        await loadAssignments();
      }
    } catch (err: any) {
      toast.error("Failed to assign: " + err.message);
    } finally {
      setDraggedStaff(null);
      setDraggedAssignment(null);
    }
  };

  const handleRemoveAssignment = async (assignmentId: number) => {
    if (!window.confirm("Remove this assignment?")) return;

    try {
      await mesApi.deleteShiftAssignment(assignmentId);
      toast.success("Assignment removed");
      await loadAssignments();
    } catch (err: any) {
      toast.error("Failed to remove: " + err.message);
    }
  };

  const getStaffingStatus = (assigned: number, required: number): string => {
    if (assigned === 0) return "critical";
    if (assigned < required) return "warning";
    return "good";
  };

  const getStaffingColor = (status: string): string => {
    if (status === "critical") return "#e74c3c";
    if (status === "warning") return "#f39c12";
    return "#27ae60";
  };

  // Get unassigned staff for the selected date/shift
  const getUnassignedStaff = (): Staff[] => {
    const assignedStaffIds = new Set(assignments.map((a) => a.staff_id));
    return staff.filter((s) => !assignedStaffIds.has(s.staff_id));
  };

  const unassignedStaff = getUnassignedStaff();

  return (
    <div className="mes-page">
      <Header title="MES - Osoblje" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />

        <div className="section mes-content">
          <ToastContainer />

          {/* Date Selector */}
          <MesDateSelector
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
          />

          {/* Shift Selector */}
          <div className="mes-section">
            <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
              <label style={{ color: "#ecf0f1", fontWeight: "500" }}>Smjena:</label>
              <select
                value={selectedShift || ""}
                onChange={(e) => setSelectedShift(Number(e.target.value))}
                className="input-classic"
                style={{ maxWidth: "300px" }}
              >
                <option value="">Odaberi smjenu</option>
                {shifts.map((shift) => (
                  <option key={shift.shift_id} value={shift.shift_id}>
                    {shift.shift_name} ({shift.start_time} - {shift.end_time})
                  </option>
                ))}
              </select>
            </div>
          </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#95a5a6" }}>
            Učitavanje...
          </div>
        ) : (
          <>
            {/* Unassigned Staff Pool */}
            <div className="mes-section">
              <h3>Dostupno osoblje ({unassignedStaff.length})</h3>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                {unassignedStaff.length === 0 ? (
                  <div style={{ color: "#95a5a6", fontSize: "14px" }}>
                    Svo osoblje dodijeljeno za ovu smjenu
                  </div>
                ) : (
                  unassignedStaff.map((person) => (
                    <div
                      key={person.staff_id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, person)}
                      style={{
                        padding: "8px 12px",
                        backgroundColor: "#34495e",
                        border: "2px solid #4a5f7f",
                        borderRadius: "4px",
                        cursor: "move",
                        color: "#ecf0f1",
                        fontSize: "14px",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#4a5f7f";
                        e.currentTarget.style.transform = "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#34495e";
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      {person.first_name} {person.last_name}
                      {person.position && (
                        <span style={{ marginLeft: "8px", color: "#95a5a6", fontSize: "12px" }}>
                          ({person.position})
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Machine Assignments */}
            <div className="mes-section">
              <h3>Raspodjela po strojevima</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "20px" }}>
              {assignmentsByMachine.map((item) => {
                const status = getStaffingStatus(item.assigned_staff.length, item.required_count);
                const statusColor = getStaffingColor(status);

                return (
                  <div
                    key={item.machine.ID}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, item.machine)}
                    style={{
                      padding: "20px",
                      backgroundColor: "#2c3e50",
                      borderRadius: "8px",
                      border: `2px solid ${statusColor}`,
                      transition: "all 0.2s",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                      <div>
                        <h4 style={{ margin: 0, color: "#ecf0f1" }}>
                          {item.machine.MachineName}
                        </h4>
                        <div style={{ fontSize: "12px", color: "#95a5a6", marginTop: "4px" }}>
                          {item.machine.MachineType}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          color: statusColor,
                        }}
                      >
                        {item.assigned_staff.length}/{item.required_count}
                      </div>
                    </div>

                    <div style={{ minHeight: "60px" }}>
                      {item.assigned_staff.length === 0 ? (
                        <div
                          style={{
                            padding: "20px",
                            textAlign: "center",
                            color: "#95a5a6",
                            fontSize: "14px",
                            border: "2px dashed #34495e",
                            borderRadius: "4px",
                          }}
                        >
                          Povuci osoblje ovdje
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {item.assigned_staff.map((assignment) => {
                            const person = staff.find((s) => s.staff_id === assignment.staff_id);
                            if (!person) return null;

                            return (
                              <div
                                key={assignment.assignment_id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, undefined, assignment)}
                                style={{
                                  padding: "8px 12px",
                                  backgroundColor: "#34495e",
                                  borderRadius: "4px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  cursor: "move",
                                }}
                              >
                                <div>
                                  <div style={{ color: "#ecf0f1", fontSize: "14px" }}>
                                    {person.first_name} {person.last_name}
                                  </div>
                                  {person.position && (
                                    <div style={{ color: "#95a5a6", fontSize: "12px" }}>
                                      {person.position}
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleRemoveAssignment(assignment.assignment_id)}
                                  style={{
                                    padding: "4px 8px",
                                    backgroundColor: "#e74c3c",
                                    border: "none",
                                    borderRadius: "4px",
                                    color: "white",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#c0392b";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#e74c3c";
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {status === "critical" && (
                      <div
                        style={{
                          marginTop: "10px",
                          padding: "8px",
                          backgroundColor: "#e74c3c33",
                          borderRadius: "4px",
                          color: "#e74c3c",
                          fontSize: "12px",
                          textAlign: "center",
                        }}
                      >
                        ⚠ Nema dodijeljenog osoblja
                      </div>
                    )}
                    {status === "warning" && (
                      <div
                        style={{
                          marginTop: "10px",
                          padding: "8px",
                          backgroundColor: "#f39c1233",
                          borderRadius: "4px",
                          color: "#f39c12",
                          fontSize: "12px",
                          textAlign: "center",
                        }}
                      >
                        ⚠ Nedovoljno osoblja (nedostaje {item.required_count - item.assigned_staff.length})
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default StaffAllocation;
